import { motion } from 'framer-motion';
import { Award, Leaf, Heart, MapPin, ArrowUpRight, User, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

export default function AboutPage() {
  const { t, language } = useLanguage();

  const values = [
    { icon: Award, title: t('about.values.quality'), desc: t('about.values.qualityDesc') },
    { icon: Heart, title: t('about.values.tradition'), desc: t('about.values.traditionDesc') },
    { icon: Leaf, title: t('about.values.sustainability'), desc: t('about.values.sustainabilityDesc') }
  ];

  const teamMembers = [
    {
      name: 'Hermann Böhmer',
      role: language === 'de' ? 'Gründer & Inhaber' : 'Founder & Owner',
      shortBio: language === 'de'
        ? 'Das Herzstück des Unternehmens – verantwortlich für Produktion, Qualität und alles, was den Namen Böhmer trägt.'
        : 'The heart of the company – responsible for production, quality, and everything that bears the Böhmer name.',
      fullBio: language === 'de'
        ? 'Hermann Böhmer ist das Herzstück von allem, was unter dem Namen Böhmer entsteht. Geboren und aufgewachsen in Dürnstein, kennt er jeden Marillenbaum der Wachau und weiß genau, wann die Früchte ihre perfekte Reife erreicht haben. Seit vielen Jahren ist er für die gesamte Produktion, die Qualitätskontrolle und die Rezepturen verantwortlich. Von der Auswahl der Marillen bis zur finalen Abfüllung liegt alles in seinen Händen. Seine langjährige Erfahrung macht ihn zum Garanten für die Qualität, die Kunden vom Weingut Hermann Böhmer erwarten.'
        : 'Hermann Böhmer is at the heart of everything created under the Böhmer name. Born and raised in Dürnstein, he knows every apricot tree in the Wachau and knows exactly when the fruits have reached their perfect ripeness. For many years he has been responsible for all production, quality control, and recipes. From selecting the apricots to the final bottling, everything is in his hands. His extensive experience makes him the guarantor of the quality that customers expect from Weingut Hermann Böhmer.',
      imageUrl: null
    },
    {
      name: 'Nicholas Böhmer',
      role: language === 'de' ? 'Mitarbeiter & zukünftiger Nachfolger' : 'Team Member & Future Successor',
      shortBio: language === 'de'
        ? 'Unterstützt seinen Vater tatkräftig im Tagesgeschäft und bereitet sich auf die Übernahme des Familienbetriebs vor.'
        : 'Actively supports his father in daily operations and is preparing to take over the family business.',
      fullBio: language === 'de'
        ? 'Nicholas ist seit seiner Kindheit Teil des Betriebs und unterstützt seinen Vater Hermann bei allen anfallenden Aufgaben. Ob bei der Ernte, in der Produktion oder beim Versand – er packt überall mit an und lernt dabei jeden Tag Neues über das Handwerk der Marillenverarbeitung. Er arbeitet Seite an Seite mit seinem Vater und bereitet sich darauf vor, eines Tages den Familienbetrieb zu übernehmen. So sorgt er dafür, dass die Tradition der Familie Böhmer auch in Zukunft weiterlebt.'
        : 'Nicholas has been part of the business since childhood and supports his father Hermann with all tasks. Whether during harvest, in production, or with shipping – he helps everywhere and learns something new every day about the craft of apricot processing. He works side by side with his father and is preparing to one day take over the family business. This ensures that the Böhmer family tradition continues into the future.',
      imageUrl: null
    },
    {
      name: 'Raphael Böhmer',
      role: language === 'de' ? 'Digitale Strategie & Webentwicklung' : 'Digital Strategy & Web Development',
      shortBio: language === 'de'
        ? 'Verantwortlich für die digitale Präsenz des Familienbetriebs – von der Website bis zur technischen Infrastruktur.'
        : 'Responsible for the digital presence of the family business – from the website to the technical infrastructure.',
      fullBio: language === 'de'
        ? 'Raphael kümmert sich um alles, was hinter den Kulissen des Familienbetriebs Weingut Hermann Böhmer digital passiert. Er hat die Website entwickelt und ist für die gesamte technische Infrastruktur verantwortlich. Als selbstständiger Unternehmer und Gründer von Astra Capital e.U. bringt er sein Know-how in Webentwicklung und digitaler Strategie ein, um das Weingut fit für die Zukunft zu machen. Dank seiner Arbeit können Kunden weltweit die Produkte aus der Wachau entdecken und bestellen.'
        : 'Raphael takes care of everything that happens digitally behind the scenes of the family business Weingut Hermann Böhmer. He developed the website and is responsible for the entire technical infrastructure. As an independent entrepreneur and founder of Astra Capital e.U., he brings his expertise in web development and digital strategy to prepare the winery for the future. Thanks to his work, customers worldwide can discover and order products from the Wachau.',
      imageUrl: null
    }
  ];

  return (
    <main className="bg-[#F9F8F6] min-h-screen pt-28 md:pt-32" data-testid="about-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 pb-12 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl"
        >
          <span className="overline">{t('about.badge')}</span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#2D2A26] mt-3 md:mt-4 leading-none">
            {t('about.title')}{' '}
            <span className="italic text-[#8B2E2E]">{t('about.titleAccent')}</span>
          </h1>
        </motion.div>
      </section>

      {/* Story Grid */}
      <section className="section-warm py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1598866971869-22782ffd918e?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Wine bottles"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#2D2A26]">
                {language === 'de' ? 'Die Geschichte beginnt in den Hängen der Wachau' : 'The story begins on the slopes of Wachau'}
              </h2>
              <p className="text-[#5C5852] text-base md:text-lg mt-6 md:mt-8 leading-relaxed">
                {t('about.story')}
              </p>
              <p className="text-[#5C5852] text-base md:text-lg mt-4 leading-relaxed">
                {language === 'de'
                  ? '1952 begann unser Großvater mit der Herstellung des ersten Likörs. Seitdem führen wir diese Tradition fort – nun in der dritten Generation.'
                  : 'In 1952, our grandfather began making the first liqueur. We have continued this tradition ever since – now in the third generation.'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section - Hermann & Nicholas */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="overline">{language === 'de' ? 'DIE MENSCHEN DAHINTER' : 'THE PEOPLE BEHIND'}</span>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2D2A26] mt-4">
              {language === 'de' ? 'Unsere Familie' : 'Our Family'}
            </h2>
            <p className="text-[#5C5852] mt-4 max-w-2xl mx-auto">
              {language === 'de' 
                ? 'Drei Generationen Leidenschaft für die Wachauer Marille.'
                : 'Three generations of passion for the Wachau apricot.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
                className="bg-white"
              >
                {/* Image Placeholder with Frame */}
                <div className="aspect-[4/5] relative bg-[#F2EFE9] border-2 border-dashed border-[#E5E0D8] flex items-center justify-center overflow-hidden">
                  {member.imageUrl ? (
                    <img 
                      src={member.imageUrl} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center p-6">
                      <User size={80} className="mx-auto text-[#C5C0B8] mb-4" />
                      <p className="text-[#969088] text-sm">
                        {language === 'de' ? 'Bild folgt' : 'Image coming'}
                      </p>
                      <p className="text-[#C5C0B8] text-xs mt-1">
                        {member.name}
                      </p>
                    </div>
                  )}
                  {/* Decorative frame corners */}
                  <div className="absolute top-3 left-3 w-10 h-10 border-t-2 border-l-2 border-[#8B2E2E]"></div>
                  <div className="absolute top-3 right-3 w-10 h-10 border-t-2 border-r-2 border-[#8B2E2E]"></div>
                  <div className="absolute bottom-3 left-3 w-10 h-10 border-b-2 border-l-2 border-[#8B2E2E]"></div>
                  <div className="absolute bottom-3 right-3 w-10 h-10 border-b-2 border-r-2 border-[#8B2E2E]"></div>
                </div>
                
                <div className="p-6 md:p-8">
                  <h3 className="font-serif text-2xl md:text-3xl text-[#2D2A26]">{member.name}</h3>
                  <p className="text-[#8B2E2E] text-sm uppercase tracking-wider mt-1">{member.role}</p>
                  <p className="text-[#5C5852] mt-4 leading-relaxed text-sm md:text-base">{member.fullBio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="section-warm py-16 md:py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <blockquote className="border-t-2 border-[#8B2E2E] pt-8 max-w-xl mx-auto text-left">
            <p className="font-quote text-xl sm:text-2xl md:text-3xl text-[#5C5852] leading-relaxed">
              &ldquo;{t('about.quote')}&rdquo;
            </p>
            <cite className="block mt-6 overline not-italic">— {t('about.family')}</cite>
          </blockquote>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2D2A26]">
              {t('about.values.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 md:p-8"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#F9F8F6] flex items-center justify-center mb-4 md:mb-6">
                  <value.icon size={24} className="text-[#8B2E2E] md:w-8 md:h-8" />
                </div>
                <h3 className="font-serif text-lg md:text-xl text-[#2D2A26]">{value.title}</h3>
                <p className="text-[#5C5852] text-sm md:text-base mt-2 md:mt-3 leading-relaxed">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline - Die Reise */}
      <section className="section-warm py-16 md:py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2D2A26]">
              {language === 'de' ? 'Unsere Reise' : 'Our Journey'}
            </h2>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-24 w-px bg-[#8B2E2E] -translate-x-px" />
            <div className="absolute left-1/2 bottom-0 h-24 w-px border-l-2 border-dashed border-[#8B2E2E]/50 -translate-x-px" />

            <div className="space-y-16 md:space-y-20">
              {[
                {
                  year: '1952',
                  titleDe: 'Grundung',
                  titleEn: 'Foundation',
                  de: 'Hermann Bohmer grundete das Weingut in Durnstein.',
                  en: 'Hermann Bohmer founded the winery in Durnstein.'
                },
                {
                  year: '1968',
                  titleDe: 'Erste Auszeichnung',
                  titleEn: 'First Award',
                  de: 'Unser Marillenlikör erhielt seine erste Pradierung.',
                  en: 'Our apricot liqueur received its first award.'
                },
                {
                  year: '1985',
                  titleDe: 'Erweiterung',
                  titleEn: 'Expansion',
                  de: 'Edelbrande und Marmeladen erweitern das Sortiment.',
                  en: 'Brandies and jams expand the product range.'
                },
                {
                  year: '2020',
                  titleDe: 'Neue Produkte',
                  titleEn: 'New Products',
                  de: 'Pralinen und Schokoladen erganzen die Produktfamilie.',
                  en: 'Pralines and chocolates join the product family.'
                },
                {
                  year: '2024',
                  titleDe: 'Digital',
                  titleEn: 'Digital',
                  de: 'Launch des Online-Shops mit weltweitem Versand.',
                  en: 'Launch of the online shop with worldwide shipping.'
                }
              ].map((item, index) => (
                <motion.div
                  key={item.year}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative grid grid-cols-2 gap-8 md:gap-16"
                >
                  <div className={`${index % 2 === 0 ? 'text-right pr-8 md:pr-12' : 'col-start-2 pl-8 md:pl-12'}`}>
                    <span className="text-[#8B2E2E] text-sm font-medium tracking-wide">{item.year}</span>
                    <h3 className="font-serif text-xl md:text-2xl text-[#2D2A26] mt-1">
                      {language === 'de' ? item.titleDe : item.titleEn}
                    </h3>
                    <p className="text-[#5C5852] text-sm md:text-base mt-2">
                      {language === 'de' ? item.de : item.en}
                    </p>
                  </div>

                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 + 0.2 }}
                    className="absolute left-1/2 top-1 w-3 h-3 rounded-full bg-[#8B2E2E] -translate-x-1/2 z-10"
                  />
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="relative grid grid-cols-2 gap-8 md:gap-16 pt-4"
              >
                <div className="col-start-2 pl-8 md:pl-12">
                  <span className="text-[#8B2E2E]/60 text-sm font-medium tracking-wide">
                    {language === 'de' ? 'Zukunft' : 'Future'}
                  </span>
                  <h3 className="font-serif text-xl md:text-2xl text-[#2D2A26] mt-1">
                    {language === 'de' ? 'Nachste Generation' : 'Next Generation'}
                  </h3>
                  <p className="text-[#5C5852] text-sm md:text-base mt-2">
                    {language === 'de'
                      ? 'Nicholas fuhrt das Erbe mit neuen Ideen weiter.'
                      : 'Nicholas continues the legacy with fresh ideas.'}
                  </p>
                </div>

                <motion.div
                  animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute left-1/2 top-5 w-4 h-4 rounded-full bg-[#8B2E2E] -translate-x-1/2 z-10 shadow-lg shadow-[#8B2E2E]/40"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <span className="overline">{language === 'de' ? 'STANDORT' : 'LOCATION'}</span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2D2A26] mt-3 md:mt-4">
                Weingut Hermann Böhmer
              </h2>
              
              <div className="flex items-start gap-3 md:gap-4 mt-6 md:mt-8 text-[#5C5852]">
                <MapPin size={20} className="text-[#8B2E2E] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-[#2D2A26] font-medium">Dürnstein 244</p>
                  <p className="text-sm md:text-base">3601 Dürnstein, Austria</p>
                </div>
              </div>

              <div className="flex gap-4 mt-8 md:mt-10">
                <Link to="/shop" className="btn-ghost inline-flex items-center gap-2">
                  {language === 'de' ? 'Zum Shop' : 'Visit Shop'}
                  <ArrowUpRight size={14} />
                </Link>
                <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                  {language === 'de' ? 'Kontaktieren' : 'Contact Us'}
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            <div className="aspect-video overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Wachau landscape"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section-warm py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-[#2D2A26] mb-4">
            {language === 'de' ? 'Haben Sie Fragen?' : 'Have Questions?'}
          </h2>
          <p className="text-[#5C5852] mb-6">
            {language === 'de' 
              ? 'Wir freuen uns auf Ihre Nachricht. Kontaktieren Sie uns gerne!'
              : 'We look forward to your message. Feel free to contact us!'}
          </p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
            {language === 'de' ? 'Kontakt aufnehmen' : 'Get in Touch'}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
